import { atom, WritableAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';

import { Namespace } from '@repo/sdk/models/components';

import type { ZoomRange } from '@/components/echart/echart.types';
import { TWENTY_FOUR_HOURS } from '@/features/trends/components/trend-group-accordion/trend-group-accordion.constants';
import { ChartSelection, DeviceAttribute } from '@/features/trends/trends.types';
import { mapIntervalToSeconds } from '@/features/trends/trends.utils';
import { formatUTCISO, subtractUTCMilliseconds } from '@/helpers/datetime/utc-date';
import { TrendSource } from '@/hooks/query-hooks/streams/gaia-api/use-trends-stream';

import { chartAtomFamily, trendGroupWithPaginationAtom } from './trend-groups.state';
import {
  DEFAULT_TOOLTIP_FONT_SIZE,
  TooltipFontSize,
} from '../components/trends-chart/components/trends-chart-settings/trends-chart-settings.constants';

/**
 * Global atom tracking whether any chart is currently being resized.
 * Used to prevent chart selection during drag operations.
 */
export const isResizingChartAtom = atom(false);
isResizingChartAtom.debugLabel = 'isResizingChartAtom';

/**
 * Default zoom range representing the full data range (0-100%).
 */
export const DEFAULT_ZOOM_RANGE: ZoomRange = { start: 0, end: 100 };

/**
 * Atom family that tracks zoom range per trend group.
 * Used to synchronize zoom state across all charts within the same group.
 * All charts in the same group share this zoom state for synchronized zooming.
 */
export const groupZoomRangeAtomFamily = atomFamily((groupId: string) => {
  const groupAtom = atom<ZoomRange>(DEFAULT_ZOOM_RANGE);
  groupAtom.debugLabel = `groupZoomRangeAtom(${groupId})`;
  return groupAtom;
});

/**
 * Write-only atom to reset a trend group's zoom to full range.
 * Use with set(resetGroupZoomAtom, groupId) or useSetAtom(resetGroupZoomAtom)(groupId).
 * Intended for user-initiated time span/interval changes; not used during streaming updates.
 */
export const resetGroupZoomAtom = atom(null, (_get, set, groupId: string) => {
  set(groupZoomRangeAtomFamily(groupId), DEFAULT_ZOOM_RANGE);
});
resetGroupZoomAtom.debugLabel = 'resetGroupZoomAtom';

/**
 * Creates a derived atom that provides read/write access to a specific chart's selection.
 * Returns undefined if the chart is not found.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * Usage:
 * ```typescript
 * const chartSelectionAtom = useMemo(() => chartSelectionAtom('chart-1'), []);
 * const [selection, setSelection] = useAtom(chartSelectionAtom);
 * ```
 *
 * Update Cascade:
 * Updating the chart selection
 *  -> updates the chart atom
 *    -> updates the trend group atom
 *      -> persists to localStorage
 */
export const chartSelectionAtom = (chartId: string) => {
  const atomInstance = atom(
    (get) => {
      const chart = get(chartAtomFamily(chartId));
      return chart?.selection;
    },
    (get, set, updated: ChartSelection) => {
      const chart = get(chartAtomFamily(chartId));
      if (!chart) return;

      set(chartAtomFamily(chartId), { ...chart, selection: updated });
    }
  );
  atomInstance.debugLabel = `chartSelectionAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that provides read/write access to device attributes in a specific chart's selection.
 * Returns an empty array if no selection exists.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * Usage:
 * ```typescript
 * const attributesAtom = useMemo(() => chartAttributesAtom('chart-1'), []);
 * const [attributes, setAttributes] = useAtom(attributesAtom);
 * ```
 *
 * Update Cascade:
 * Updating the chart attributes
 *  -> updates the chart selection atom
 *    -> updates the chart atom
 *      -> updates the trend group atom
 *        -> persists to localStorage
 */
export const chartAttributesAtom = (chartId: string) => {
  const atomInstance = atom(
    (get) => {
      const selection = get(chartSelectionAtom(chartId));
      return selection?.attributes ?? [];
    },
    (get, set, updated: DeviceAttribute[]) => {
      const selection = get(chartSelectionAtom(chartId));
      if (!selection) return;

      set(chartSelectionAtom(chartId), { ...selection, attributes: updated });
    }
  );
  atomInstance.debugLabel = `chartAttributesAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that filters a specific chart's attributes to only include settings.
 * Settings are identified by having a namespace of Namespace.Setting.
 * Returns an empty array if no attributes exist.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * This is a read-only atom.
 *
 * Usage:
 * ```typescript
 * const settingsAtom = useMemo(() => chartSettingsAtom('chart-1'), []);
 * const settings = useAtomValue(settingsAtom);
 * ```
 */
export const chartSettingsAtom = (chartId: string) => {
  const atomInstance = atom((get) => {
    const attributes = get(chartAttributesAtom(chartId));
    return attributes.filter(
      (attr: DeviceAttribute) => attr.displayRef.ref.namespace === Namespace.Setting
    );
  });
  atomInstance.debugLabel = `chartSettingsAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that filters a specific chart's attributes to only include readings.
 * Readings are any attributes that are NOT settings (i.e., different namespace).
 * Returns an empty array if no attributes exist.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * This is a read-only atom.
 *
 * Usage:
 * ```typescript
 * const readingsAtom = useMemo(() => chartReadingsAtom('chart-1'), []);
 * const readings = useAtomValue(readingsAtom);
 * ```
 */
export const chartReadingsAtom = (chartId: string) => {
  const atomInstance = atom((get) => {
    const attributes = get(chartAttributesAtom(chartId));
    return attributes.filter(
      (attr: DeviceAttribute) => attr.displayRef.ref.namespace !== Namespace.Setting
    );
  });
  atomInstance.debugLabel = `chartReadingsAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that provides read/write access to expanded sections in a specific chart's selection.
 * Returns an empty array if no selection exists.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * Usage:
 * ```typescript
 * const expandedSectionsAtom = useMemo(() => chartExpandedSectionsAtom('chart-1'), []);
 * const [expandedSections, setExpandedSections] = useAtom(expandedSectionsAtom);
 * ```
 *
 * Update Cascade:
 * Updating the chart expanded sections
 *  -> updates the chart selection atom
 *    -> updates the chart atom
 *      -> updates the trend group atom
 *        -> persists to localStorage
 */
export const chartExpandedSectionsAtom = (chartId: string) => {
  const atomInstance = atom(
    (get) => {
      const selection = get(chartSelectionAtom(chartId));
      return selection?.expandedSections ?? [];
    },
    (get, set, updated: string[]) => {
      const selection = get(chartSelectionAtom(chartId));
      if (!selection) return;

      set(chartSelectionAtom(chartId), { ...selection, expandedSections: updated });
    }
  );
  atomInstance.debugLabel = `chartExpandedSectionsAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that provides read/write access to search term in a specific chart's selection.
 * Returns an empty string if no selection exists.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * Usage:
 * ```typescript
 * const searchTermAtom = useMemo(() => chartSearchTermAtom('chart-1'), []);
 * const [searchTerm, setSearchTerm] = useAtom(searchTermAtom);
 * ```
 *
 * Update Cascade:
 * Updating the chart search term
 *  -> updates the chart selection atom
 *    -> updates the chart atom
 *      -> updates the trend group atom
 *        -> persists to localStorage
 */
export const chartSearchTermAtom = (chartId: string) => {
  const atomInstance = atom(
    (get) => {
      const selection = get(chartSelectionAtom(chartId));
      return selection?.searchTerm ?? '';
    },
    (get, set, updated: string) => {
      const selection = get(chartSelectionAtom(chartId));
      if (!selection) return;

      set(chartSelectionAtom(chartId), { ...selection, searchTerm: updated });
    }
  );
  atomInstance.debugLabel = `chartSearchTermAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that provides read/write access to display mode in a specific chart.
 * Returns 'actual' as default if no display mode is set.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * Usage:
 * ```typescript
 * const displayModeAtom = useMemo(() => chartDisplayModeAtom('chart-1'), []);
 * const [displayMode, setDisplayMode] = useAtom(displayModeAtom);
 * ```
 *
 * Update Cascade:
 * Updating the chart display mode
 *  -> updates the chart atom
 *    -> updates the trend group atom
 *      -> persists to localStorage
 */
export const chartDisplayModeAtom = (chartId: string) => {
  const atomInstance = atom(
    (get) => {
      const chart = get(chartAtomFamily(chartId));
      return chart?.displayMode ?? 'actual';
    },
    (get, set, updated: 'actual' | 'normalized') => {
      const chart = get(chartAtomFamily(chartId));
      if (!chart) return;

      set(chartAtomFamily(chartId), { ...chart, displayMode: updated });
    }
  );
  atomInstance.debugLabel = `chartDisplayModeAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a read-only atom that aggregates all charts in a group into a single
 * subscription payload. Source IDs are deduplicated via Map to avoid redundant ECP work
 * while preserving value_kind.
 *
 * When any chart adds/removes an attribute or changes display mode, or when the group
 * timespan/pagination changes, this atom recomputes and triggers a re-subscription.
 *
 * IMPORTANT: MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * @param groupId - The unique identifier for the trend group
 */
export const groupSubscriptionAtom = (groupId: string) => {
  const atomInstance = atom((get) => {
    const group = get(trendGroupWithPaginationAtom(groupId));
    if (!group) return null;

    // Deduplicate sources by ID using Map (preserves value_kind from last occurrence)
    const readingSourceMap = new Map<string, TrendSource>();
    const settingSourceMap = new Map<string, TrendSource>();
    let includeNormalized = false;

    for (const chart of group.charts) {
      const readings = get(chartReadingsAtom(chart.id)) ?? [];
      const settings = get(chartSettingsAtom(chart.id)) ?? [];
      const displayMode = get(chartDisplayModeAtom(chart.id));

      for (const attr of readings) {
        readingSourceMap.set(attr.displayRef.ref.id, {
          id: attr.displayRef.ref.id,
          value_kind: attr.displayRef.ref.valueKind,
        });
      }
      for (const attr of settings) {
        settingSourceMap.set(attr.displayRef.ref.id, {
          id: attr.displayRef.ref.id,
          value_kind: attr.displayRef.ref.valueKind,
        });
      }
      if (displayMode === 'normalized') includeNormalized = true;
    }

    const allReadingSources = Array.from(readingSourceMap.values());
    const allSettingSources = Array.from(settingSourceMap.values());

    if (allReadingSources.length === 0 && allSettingSources.length === 0) return null;

    const interval = mapIntervalToSeconds(group.interval ?? 'per-second');
    const start = formatUTCISO(group.start ?? subtractUTCMilliseconds(TWENTY_FOUR_HOURS));
    const end = group.end ? formatUTCISO(group.end) : undefined;

    return {
      subscription_id: groupId,
      subscription: {
        start,
        end,
        interval,
        reading_sources: allReadingSources,
        setting_sources: allSettingSources,
        include_normalized: includeNormalized,
      },
    };
  });
  atomInstance.debugLabel = `groupSubscriptionAtom(${groupId})`;
  return atomInstance;
};

/**
 *
 * @param chartId - The unique identifier for the chart
 * @returns The tooltip font size for the chart
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * This is a read-only atom.
 *
 * Usage:
 * ```typescript
 * const tooltipFontSizeAtom = useMemo(() => chartTooltipFontSizeAtom('chart-1'), []);
 * const tooltipFontSize = useAtomValue(tooltipFontSizeAtom);
 * ```
 *
 * Update Cascade:
 * Updating the tooltip font size
 *  -> updates the chart atom
 *    -> updates the trend group atom
 *      -> persists to localStorage
 */
export const chartTooltipFontSizeAtom: (
  chartId: string
) => WritableAtom<TooltipFontSize, [updated: TooltipFontSize], void> = (chartId: string) => {
  const atomInstance = atom(
    (get) => {
      const chart = get(chartAtomFamily(chartId));
      return chart?.tooltipFontSize ?? DEFAULT_TOOLTIP_FONT_SIZE;
    },
    (get, set, updated: TooltipFontSize) => {
      const chart = get(chartAtomFamily(chartId));
      if (!chart) return;

      set(chartAtomFamily(chartId), { ...chart, tooltipFontSize: updated });
    }
  );
  atomInstance.debugLabel = `chartTooltipFontSizeAtom(${chartId})`;
  return atomInstance;
};

/**
 * Creates a derived atom that provides read/write access to Y-axis min/max configuration for a specific chart.
 * Returns an empty Map as default if no configuration exists.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * Usage:
 * ```typescript
 * const yAxisMinMaxAtom = useMemo(() => chartYAxisMinMaxAtom('chart-1'), []);
 * const [yAxisMinMax, setYAxisMinMax] = useAtom(yAxisMinMaxAtom);
 * ```
 *
 * Update Cascade:
 * Updating the chart Y-axis min/max
 *  -> updates the chart atom
 *    -> updates the trend group atom
 *      -> persists to localStorage
 */
export const chartYAxisMinMaxAtom = (
  chartId: string
): WritableAtom<
  Record<number, { min?: number; max?: number }>,
  [updated: Record<number, { min?: number; max?: number }>],
  void
> => {
  const atomInstance = atom(
    (get) => {
      const chart = get(chartAtomFamily(chartId));
      return chart?.yAxisMinMax ?? {};
    },
    (get, set, updated: Record<number, { min?: number; max?: number }>) => {
      const chart = get(chartAtomFamily(chartId));
      if (!chart) return;

      set(chartAtomFamily(chartId), { ...chart, yAxisMinMax: updated });
    }
  );
  atomInstance.debugLabel = `chartYAxisMinMaxAtom(${chartId})`;
  return atomInstance;
};
